'use client';

import {getAllCompanies} from 'iq-companies/api';
import {MultiComboboxInput, MultiComboboxOption} from 'common/components/multi_combobox_input';
import type {OnboardingCompany} from 'iq-onboarding/types';
import {
  trackCompaniesSubmitted,
  trackCompanyAdded,
  trackCompanyRemoved,
} from 'iq-onboarding/tracking';
import {X} from 'lucide-react';
import Image from 'next/image';
import {type ReactNode} from 'react';

type CompanyOption = MultiComboboxOption & {
  company: OnboardingCompany;
};

async function fetchCompanyOptions(): Promise<CompanyOption[]> {
  const companies = await getAllCompanies();
  return companies.map((c) => ({
    value: c.slug,
    label: c.name,
    company: c,
  }));
}

// Selected companies render as pills showing the company logo.
function renderCompanyChips(
  options: CompanyOption[],
  onRemove: (value: string) => void,
): ReactNode {
  return (
    <div className='flex flex-wrap gap-2'>
      {options.map((o) => (
        <span
          key={o.company.slug}
          className='inline-flex items-center gap-2 pl-1.5 pr-2.5 py-1 bg-white border border-iq-grey-light rounded-full text-sm font-medium text-iq-black'>
          {o.company.icon_img ? (
            <Image
              src={o.company.icon_img}
              alt={o.company.name}
              width={20}
              height={20}
              className='w-5 h-5 rounded object-contain'
              unoptimized
            />
          ) : null}
          {o.company.name}
          <button
            type='button'
            onClick={() => onRemove(o.value)}
            className='text-iq-grey-medium hover:text-iq-black transition-colors'
            aria-label={`Remove ${o.company.name}`}>
            <X className='w-3.5 h-3.5' />
          </button>
        </span>
      ))}
    </div>
  );
}

interface CompaniesStepProps {
  value: OnboardingCompany[];
  onChange: (values: OnboardingCompany[]) => void;
  onContinue: () => void;
}

export function CompaniesStep({value, onChange, onContinue}: CompaniesStepProps) {
  const canContinue = value.length >= 1;

  // The combobox hands back the full new selection; diff it to track the single
  // company that was added or removed.
  function handleChange(next: OnboardingCompany[]) {
    const prevSlugs = new Set(value.map((c) => c.slug));
    const nextSlugs = new Set(next.map((c) => c.slug));
    const added = next.find((c) => !prevSlugs.has(c.slug));
    const removed = value.find((c) => !nextSlugs.has(c.slug));
    if (added) void trackCompanyAdded(added.slug, added.name);
    if (removed) void trackCompanyRemoved(removed.slug, removed.name);
    onChange(next);
  }

  return (
    <div className='flex flex-col gap-6'>
      <div>
        <div className='flex flex-col justify-end lg:h-[4.5rem]'>
          <h1 className='text-2xl sm:text-3xl font-extrabold text-white'>
            Which companies are you interviewing for?
          </h1>
        </div>
        <p className='text-white/60 text-base mt-2'>Select the companies you&apos;re interested in.</p>
      </div>

      <div className='bg-white rounded-2xl p-6 flex flex-col gap-4'>
        <MultiComboboxInput
          label='Target companies'
          placeholder='Search by company name...'
          value={value.map((c) => c.slug)}
          onChange={(os) => handleChange(os.map((o) => o.company))}
          fetchOptions={fetchCompanyOptions}
          renderSelected={renderCompanyChips}
        />
      </div>

      <button
        type='button'
        disabled={!canContinue}
        onClick={() => {
          void trackCompaniesSubmitted(value.map((c) => c.slug));
          onContinue();
        }}
        className='w-full py-4 px-8 bg-iq-orange text-white text-base font-bold rounded-full hover:opacity-90 transition-opacity disabled:opacity-30'>
        Continue
      </button>
    </div>
  );
}
